import { Timeline } from "@/components/Timeline";
import { QuiltPlannerHeader } from "@/components/QuiltPlannerHeader";
import { QuiltPlannerFooter } from "@/components/QuiltPlannerFooter";
import { GlobalRecentPlans } from "@/components/GlobalRecentPlans";
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import { Card, CardContent, Box, Typography, Container } from "@mui/material";
import { Bed, Weekend, MoneyOff, Celebration, Devices, Launch } from '@mui/icons-material';
import { cardContentSx } from '@/utils/sharedStyles';
import { Helmet } from "react-helmet-async";
import heroImage from "@/assets/hero-image.png";
const timelineSteps = [{
  number: 1,
  title: "Purpose",
  description: "Choose bed cover or throw blanket."
}, {
  number: 2,
  title: "Size",
  description: "Choose from international bed sizes. Or, add a custom throw blanket size."
}, {
  number: 3,
  title: "Design",
  description: "Add what size you want your finished blocks to be. Add sashing, cornerstones, and borders if you like."
}, {
  number: 4,
  title: "Fabric settings",
  description: "Change default settings like what size fabric you'll buy, how much it will cost, and the seam allowance."
}, {
  number: 5,
  title: "Quilt plan",
  description: "Bookmark the link or copy the plan to your Notes app. Links stay active for 90 days.",
  isCheck: true
}];
const LandingPage = () => {
  const navigate = useNavigate();
  const handleStartPlanning = (purpose: 'bed-cover' | 'throw-blanket') => {
    // Track Google Analytics event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'start_planning', {
        event_category: 'navigation',
        method: purpose,
        purpose: purpose
      });
    }
    navigate('/planner', {
      state: {
        quiltData: {
          purpose
        },
        initialStep: 1 // Start on step 2 (0-indexed)
      }
    });
  };
  const handleGetQuiltPlan = () => {
    // Track Google Analytics event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'start_planning', {
        event_category: 'navigation',
        method: 'get_plan_button'
      });
    }
    navigate('/planner', {
      state: {
        initialStep: 0 // Start on step 1 (0-indexed)
      }
    });
  };
  return <>
      <Helmet>
        <title>Plan a quilt | Quilt math, done for you.</title>
        <meta name="description" content="Free quilt planning calculator that does your quilt math. Calculate blocks, fabric yardage, and costs for bed covers and throw blankets. No sign-up required." />
        <meta name="keywords" content="quilt calculator, quilt planner, quilt math, fabric calculator, quilt size calculator, free quilt planner, quilting tool, bed quilt calculator" />
        <meta property="og:title" content="Plan a quilt | Quilt math, done for you." />
        <meta property="og:description" content="Free quilt planning calculator that does your quilt math. Calculate blocks, fabric yardage, and costs instantly." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://plan-a-quilt.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Plan a quilt | Quilt math, done for you." />
        <meta name="twitter:description" content="Free quilt planning calculator that does your quilt math. Calculate blocks, fabric yardage, and costs instantly." />
        <link rel="canonical" href="https://plan-a-quilt.com/" />
      </Helmet>
      <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
        <QuiltPlannerHeader showClear={false} onClear={() => {}} />
      
      <Box component="main" sx={{
        flex: 1,
        backgroundColor: 'background.default'
      }}>
        {/* Hero Section */}
        <Container maxWidth="lg" sx={{
          px: 2,
          pt: {
            xs: '36px',
            md: 0
          },
          pb: 0
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            gap: {
              xs: 4,
              md: 6
            },
            alignItems: {
              xs: 'flex-start',
              md: 'center'
            }
          }}>
            {/* Left: Content */}
            <Box sx={{
              flex: 1,
              textAlign: {
                xs: 'center',
                md: 'left'
              }
            }}>
              <Typography variant="h1" component="h1" sx={{
                fontSize: {
                  xs: '2.25rem',
                  md: '3rem'
                },
                fontWeight: 'bold',
                color: 'text.primary',
                mb: 3
              }}>
                Quilt math, done for you.
              </Typography>
              
              <Typography variant="h2" component="h2" sx={{
                color: 'text.primary',
                fontWeight: 300,
                lineHeight: 1.5,
                mb: 3,
                maxWidth: {
                  xs: '100%',
                  md: '48rem'
                },
                mx: {
                  xs: 'auto',
                  md: 0
                }
              }}>Don't know what size to make your quilt? How many blocks to make? Start here.</Typography>
              
              <Box sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: 2,
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start'
                }
              }}>
                <Button variant="contained" color="primary" onClick={() => handleStartPlanning('bed-cover')} startIcon={<Bed aria-hidden="true" focusable="false" />} sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  textTransform: 'none',
                  width: {
                    xs: '100%',
                    sm: 'auto'
                  },
                  borderRadius: 2,
                  minHeight: '44px'
                }}>Plan a bed cover</Button>
                <Button variant="contained" color="primary" onClick={() => handleStartPlanning('throw-blanket')} startIcon={<Weekend aria-hidden="true" focusable="false" />} sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  textTransform: 'none',
                  width: {
                    xs: '100%',
                    sm: 'auto'
                  },
                  borderRadius: 2,
                  minHeight: '44px'
                }}>Plan a throw blanket</Button>
              </Box>
              
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                mt: 3,
                textAlign: {
                  xs: 'center',
                  md: 'left'
                }
              }}>It's free. No accounts or downloads needed.</Typography>
            </Box>

            {/* Right: Hero Image */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              maxWidth: {
                xs: '100%',
                md: '420px'
              }
            }}>
              <img src={heroImage} alt="Quilter planning a quilt with organized fabric squares" style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }} />
            </Box>
          </Box>
        </Container>

       {/* Recent Plans Section */}
        <GlobalRecentPlans />
        
        {/* How it works section */}
        <Box sx={{
          py: 8,
          backgroundColor: 'background.default'
        }}>
          <Container maxWidth="lg" sx={{
            px: 2
          }}>
            <Typography variant="h2" component="h2" sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: '2rem'
            }}>How to build a plan</Typography>
            
            <Typography variant="body1" sx={{
              color: 'text.secondary',
              textAlign: 'center',
              mb: 6
            }}>Fill out a simple 4-step form.</Typography>
            
            <Box sx={{
              mx: 'auto',
              position: 'relative',
              maxWidth: '500px'
            }}>
              <Timeline steps={timelineSteps} />
            </Box>
            
            <Box sx={{
              textAlign: 'center',
              mt: 4
            }}>
              <Button variant="contained" color="primary" onClick={handleGetQuiltPlan} sx={{
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '1.125rem',
                textTransform: 'none',
                borderRadius: 2,
                minHeight: '44px'
              }}>
                Start quilt plan
              </Button>
            </Box>
          </Container>
        </Box>
        

        {/* Who's behind this quilt planner section */}
        <Box sx={{
          backgroundColor: 'primary.light',
          py: 8
        }}>
          <Container maxWidth="lg" sx={{
            px: 2
          }}>
            <Typography variant="h2" component="h2" sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: '2rem'
            }}>
              Who's behind this quilt planner?
            </Typography>
            
            <Typography variant="body1" sx={{
              color: 'text.secondary',
              textAlign: 'center',
              mb: 6
            }}>Made for fun by Emily in Asheville, NC, USA.</Typography>
            
            <Box sx={{
              maxWidth: '64rem',
              mx: 'auto'
            }}>
              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'auto 1fr'
                },
                gap: 4,
                alignItems: 'center',
                maxWidth: '750px',
                mx: 'auto'
              }}>
                <Box sx={{
                  width: {
                    xs: '100%',
                    md: '260px'
                  },
                  aspectRatio: '1',
                  overflow: 'hidden',
                  borderRadius: 2
                }}>
                  <Box component="img" src={new URL('@/assets/emily-quilt.jpg', import.meta.url).href} alt="Emily's colorful quilt with star patterns" sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }} />
                </Box>
                
                <Box>
                  
                  <Typography variant="body1" sx={{
                    color: 'text.primary',
                    textAlign: 'left'
                  }}>
                    I created this quilt planner because I needed help with my quilt math and I wanted to learn how to use AI to build websites. I made it available for free because it's a fun project to work on and I think it's really useful! I don't make money from it or collect personal data.
                  </Typography>
                  
                  <Typography variant="body1" sx={{
                    color: 'text.primary',
                    textAlign: 'left',
                    mt: 2
                  }}>
                    Want to support this project?{' '}
                    <Box component="a" href="https://docs.google.com/forms/d/e/1FAIpQLScRJtzvGLaC22oTmgbU4Us7MTRIaOFjNdx3cU4_3HRNKp1hUg/viewform?usp=header" target="_blank" rel="noopener noreferrer" sx={{
                      color: 'primary.main',
                      textDecoration: 'underline',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        textDecoration: 'none'
                      }
                    }}>
                      Leave feedback
                      <Launch sx={{ fontSize: '1rem' }} />
                    </Box>
                  </Typography>
                </Box>
              
              </Box>
              
              
              
            </Box>
          </Container>
        </Box>
        
      </Box>

        <QuiltPlannerFooter />
      </Box>
    </>;
};
export default LandingPage;